import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';
import { useFeatureIsOn } from '@growthbook/growthbook-react';

import { Button, Icon } from '@gumtree/ui-library';
import { qaAttribute } from '@gumtree/ui-library/src/utils/qa-service';
import { trackV2 } from '@gumtree/shared/src/util/track-ga-event';
import { fontSizes } from '@gumtree/ui-library/src/base/theme';
import { trackGA4Event } from '@gumtree/shared/src/util/ga4-shared';
import { GrowthBookFeature } from '@gumtree/shared/src/model/experiment';
import { POLLING_INTERVAL_MS } from '@gumtree/shared/src/constants/message-centre';
import {
    MhhNavBarButtonOverride,
    NavBarButtonOverride,
} from '../nav-bar/common.style';
import { MhhNavIcon } from '../nav-bar/style';
import {
    iconButtonCss,
    IconLabel,
    MenuButtonWrapper,
    UnreadBadge,
} from '../nav-bar/mhh-nav-bar/mhh-nav-bar.style';
import type { ShellState, ShellUseDispatch } from '../../reducers/common';
import { fetchUnreadMessagesCount } from '../../reducers/common/unread-messages';
import logger from '../../logger';

export const StyledButton = styled(Button, {
    shouldForwardProp: (p: string) => p !== 'mhhStyle',
})<{ mhhStyle?: boolean }>`
    && {
        ${NavBarButtonOverride}
        ${({ mhhStyle }) => mhhStyle && MhhNavBarButtonOverride}
    }

    > [aria-hidden="true"] {
        font-size: ${fontSizes.pgLarge};
    }
`;

interface SideNavButtonProps {
    dark?: boolean;
    onClick?: () => void;
    isMhhExperiment?: boolean;
    isLoggedIn?: boolean;
    useMhhLayout?: boolean;
}

export const SideNavButton: React.FC<SideNavButtonProps> = ({
    dark = false,
    onClick = () => {},
    isMhhExperiment = false,
    isLoggedIn = false,
    useMhhLayout = false,
}) => {
    const dispatch = useDispatch() as ShellUseDispatch;
    const unreadMessagesCount = useSelector(
        ({ unreadMessagesCount }: ShellState) => unreadMessagesCount
    );
    const showUnreadCount = useFeatureIsOn(GrowthBookFeature.UNREAD_FEATURE_FLAG);
    const hasUnread = isMhhExperiment && isLoggedIn && showUnreadCount && unreadMessagesCount > 0;
    const unreadMessagesCountToDisplay = Math.min(unreadMessagesCount, 99);

    useEffect(() => {
        if (!isMhhExperiment || !isLoggedIn || !useMhhLayout || !showUnreadCount) {
            return undefined;
        }

        let intervalId = 0;
        async function intervalCallback() {
            try {
                const shouldPoll = await dispatch(fetchUnreadMessagesCount());
                if (!shouldPoll) {
                    clearInterval(intervalId);
                }
            } catch (error: any) {
                logger.error('Error fetching unread messages count:', error);
            }
        }

        intervalId = window.setInterval(intervalCallback, POLLING_INTERVAL_MS);
        intervalCallback();

        return () => clearInterval(intervalId);
    }, [dispatch, isLoggedIn, isMhhExperiment, showUnreadCount, useMhhLayout]);

    const handleClick = () => {
        onClick();
        trackGA4Event<GA4.ClickMobileBurgerMenu>({
            event: 'click_mobile_burger_menu_icon',
            clickMenuAction: 'open',
            breakpointSize: 'mobile',
        });
        trackV2('HeaderMenuSelect');
    };

    if (useMhhLayout) {
        return (
            <MenuButtonWrapper>
                <button
                    type="button"
                    css={iconButtonCss}
                    onClick={handleClick}
                    aria-label="Menu"
                    {...qaAttribute('side-nav-button-mobile')}
                >
                    <Icon type="navbar-menu" />
                    <IconLabel>Menu</IconLabel>
                </button>
                {hasUnread && (
                    <UnreadBadge data-testid="mhh-menu-unread-badge">
                        {unreadMessagesCountToDisplay}
                    </UnreadBadge>
                )}
            </MenuButtonWrapper>
        );
    }

    return (
        <StyledButton
            bottomLabel
            dark={dark}
            icon={isMhhExperiment ? <MhhNavIcon type="navbar-menu" /> : <Icon type="burger" />}
            label="Menu"
            onClick={handleClick}
            mhhStyle={isMhhExperiment}
            {...qaAttribute('side-nav-button-mobile')}
        />
    );
};

export default SideNavButton;
